import { Dropdown } from "react-bootstrap"
import useDropdown from "../../../hooks/dropdown.hook";
import { createContext, useContext, useEffect, ReactNode, RefObject } from "react";
import cn from "../../../Utils/class-names.utils";
import './my-dropdown.styles.scss';

interface DropdownContextValue {
  setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  refBtn: RefObject<any>;
  refMenu: RefObject<any>;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

interface MyDropdownProps {
  className?: string;
  toggleClassName?: string;
  menuClassName?: string;
  onToggle?: (isOpen: boolean) => void;
  toggleComponent?: ReactNode;
  children?: ReactNode;
  popperConfig?: any;
  isOpenUpdate?: (isOpen: boolean) => void;
  refBtn?: RefObject<any>;
  refMenu?: RefObject<any>;
  setRefBtn?: (ref: RefObject<any>) => void;
  arrowFill?: string;
  arrowWidth?: number | string;
  arrowHeight?: number | string;
  renderOnMount?: boolean;
  alignRight?: boolean;
  [key: string]: any;
}

/**
 * MyDropdown component
 * @param {Object} props
 * @param {string} props.className
 * @param {string} props.toggleClassName
 * @param {string} props.menuClassName
 * @param {function} props.onToggle
 * @param {React.Component} props.toggleComponent
 * @param {React.Component} props.children
 * @param {object} props.popperConfig
 * @param {function} props.isOpenUpdate
 * @param {React.RefObject} props.refBtn
 * @param {React.RefObject} props.refMenu
 * @returns {React.Component}
 * @example
 * <MyDropdown
      toggleComponent={(
        <div className="f-black">
          Menu
        </div>
      )}
      dropdownClassName="ml-3"
    >
      <MyDropdown.Item>
        Item 1
      </MyDropdown.Item>
      <MyDropdown.Item>
        Item 2
      </MyDropdown.Item>
      <MyDropdown.Item>
        Item 3
      </MyDropdown.Item>
    </MyDropdown>
 */
const MyDropdown = ({
  className = "",
  toggleClassName = "",
  menuClassName = "",
  onToggle = (isOpen: boolean) => { },
  toggleComponent,
  children,
  popperConfig,
  isOpenUpdate = (isOpen: boolean) => { },
  refBtn,
  refMenu,
  setRefBtn = (ref: RefObject<any>) => {},
  arrowFill,
  arrowWidth,
  arrowHeight,
  renderOnMount,
  alignRight,
  ...rest
}: MyDropdownProps) => {
  const { isOpen, refBtn: curRefBtn, refMenu: curRefMenu, setIsOpen } = useDropdown({ 
    disableClickOutside: false,
    defaultRefBtn: (refBtn?.current || null) as any,
    defaultRefMenu: (refMenu?.current || null) as any
  });

  useEffect(() => {
    setRefBtn(curRefBtn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curRefBtn]);

  useEffect(() => {
    onToggle(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  
  return (
    <DropdownContext.Provider value={{ setIsOpen, refBtn: curRefBtn, refMenu: curRefMenu }}>
      <Dropdown
        {...rest}
        className={cn('transparennt-wrapper-dropdown my-dropdown my-dropdown-common', className)}
        onToggle={() => {
          setIsOpen(prev => !prev);
          isOpenUpdate(!isOpen);
        }}
        show={isOpen}
      >
        {children}
      </Dropdown>
    </DropdownContext.Provider>
  )
}

interface MyDropdownToggleProps {
  className?: string;
  children?: ReactNode;
  hideArrow?: boolean;
  [key: string]: any;
}

const MyDropdownToggle = ({ className, children, hideArrow = false, ...props }: MyDropdownToggleProps) => {
  const context = useContext(DropdownContext);
  if (!context) throw new Error('MyDropdownToggle must be used within MyDropdown');
  const { refBtn } = context;
  return (
    <Dropdown.Toggle className={cn('d-flex align-items-center', className)} {...props} ref={refBtn}>
      {children}
      {!hideArrow && (
        <div className="arrow-icon" />
      )}
    </Dropdown.Toggle>
  )
}

interface MyDropdownMenuProps {
  className?: string;
  children?: ReactNode;
  [key: string]: any;
}

const MyDropdownMenu = ({ className = "", children, ...props }: MyDropdownMenuProps) => {
  const context = useContext(DropdownContext);
  if (!context) throw new Error('MyDropdownMenu must be used within MyDropdown');
  const { refMenu } = context;
  return (
    <Dropdown.Menu className={cn('menu', className)} {...props} ref={refMenu}>
      {children}
    </Dropdown.Menu>
  )
}

interface MyDropdownItemProps {
  className?: string;
  children?: ReactNode;
  closeOnClick?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  [key: string]: any;
}

const MyDropdownItem = ({ className, children, closeOnClick = true, disabled, ...props }: MyDropdownItemProps) => {
  const context = useContext(DropdownContext);
  if (!context) throw new Error('MyDropdownItem must be used within MyDropdown');
  const { setIsOpen } = context;

  return (
    <div 
      {...props} 
      className={`item ${className ?? ''} ${disabled ? 'disabled' : ''}`} 
      onClick={(e) => {
        if (disabled) return
        if (closeOnClick) {
          setIsOpen(false);
        }
        if (props.onClick) {
          props.onClick(e);
        }
      }}
    >
      {children}
    </div>
  );
};

/**
 * MyDropdown.Item component
 * 
 * @param {Object} props
 * @param {string} className
 * @param {Function} onClick
 * @param {boolean} closeOnClick
 * @param {boolean} disabled
 * @param {React.Component} children
 * @returns {React.Component}
 */
MyDropdown.Item = MyDropdownItem;
MyDropdown.Toggle = MyDropdownToggle;
MyDropdown.Menu = MyDropdownMenu;

export default MyDropdown;
